import {DeleteFilled, InfoCircleOutlined, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, message, Modal, Space, Table, Tooltip} from "antd";
import _ from "lodash";
import React, {useContext, useEffect, useMemo, useState} from "react";
import Organization, {aiTierCreditLimit} from "../../domain/Organization";
import {DocumentTitle} from "../DocumentTitle";
import {Link, useNavigate} from "react-router-dom";
import {AppContextContext, OrganizationServiceContext} from "../../Contexts";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

import * as styles from "./OrganizationList.module.css";
import * as globalStyles from "../App.module.css";
import {ColumnsType} from "antd/es/table";
import {Feature, formatFeature} from "../../domain/Feature";
import AiCreditUsageIndicator from "../common/AiCreditUsageIndicator";
import HashColorTag from "../common/HashColorTag";
import OrganizationFilter from "./OrganizationFilter";
import {FormPersister} from "../../sal-ui/FormPersister";

const ACTIVE_USER_DAYS = 30;
const FILTER_DEBOUNCE_MS = 500;
const PERSISTENT_IDENT = "OrganizationList";

function OrganizationList() {
    const appContext = useContext(AppContextContext);
    const organizationService = useContext(OrganizationServiceContext);
    const navigate = useNavigate();
    const tableHandler = useTableHandler("name", {reloadFunction: reload});
    const [organizations, setOrganizations] = useState<PagedResult<Organization>>();
    const [filterValues, setFilterValues] = useState<any>();

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        reload();
    }, [])

    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);

    const columns: ColumnsType<Organization> = [
        {
            dataIndex: "name",
            title: "Name",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            defaultSortOrder: "ascend",
            render: renderName
        },
        {
            dataIndex: "features",
            title: "Features",
            render: (features, organization) => features?.map((value: Feature) =>
                <div key={value}>
                    {formatFeature(value)}
                    {value === Feature.AI && organization.aiTier && <> ({organization.aiTier.name})</>}
                </div>
            )
        },
        {
            title: "AI credits",
            width: 200,
            render: (_: any, organization: Organization) => {
                if (!organization.features?.includes(Feature.AI) || !organization.aiTier) {
                    return null;
                }

                const used = organization.creditsUsedThisMonth ?? 0;
                const tierLimit = aiTierCreditLimit(organization) ?? 0;

                return <AiCreditUsageIndicator used={used} limit={tierLimit}/>;
            }
        },
        {
            dataIndex: "userCount",
            title: "Users",
            width: 80,
            align: "center",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            render: value => value ?? 0
        },
        {
            dataIndex: "activeUserCount",
            title: (
                <span>
                    Active users{" "}
                    <Tooltip title={`Users who logged in at least once in the last ${ACTIVE_USER_DAYS} days.`}>
                        <InfoCircleOutlined/>
                    </Tooltip>
                </span>
            ),
            width: 145,
            align: "center",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            render: value => value ?? 0
        },
        {
            dataIndex: "vulReportDefinitionCount",
            title: "Report definitions",
            width: 150,
            align: "center",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            render: renderVulReportDefinitionCount
        },
        {
            title: "Actions",
            width: 100,
            className: globalStyles["table-actions"],
            render: renderAction
        }
    ]

    const title = "Organizations";

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item>{title}</Breadcrumb.Item>
                </Breadcrumb>

                <h1>{title}</h1>

                <OrganizationFilter
                    className={styles.filter}
                    values={filterValues}
                    persistentStateIdent={PERSISTENT_IDENT}
                    onChange={values => {
                        setFilterValues(values);

                        debouncedOnFinishFilter(values);
                    }}
                />

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
                    <Button type={"primary"}
                            icon={<PlusOutlined/>}
                            onClick={() => navigate("/organizations/add")}>Add</Button>
                </div>

                <Table className={styles.table}
                       showSorterTooltip={false}
                       loading={tableHandler.loading}
                       dataSource={organizations?.data}
                       size="middle"
                       onChange={tableHandler.onTableChange}
                       pagination={tableHandler.pagination}
                       onRow={onRow}
                       rowKey="id"
                       columns={columns}
                />
            </>
        </DocumentTitle>
    )

    function renderName(value: any, organization: Organization) {
        const tags = organization.tags ?? [];

        return (
            <>
                <Link to={`/organizations/${organization.id}`}>{value}</Link>
                {tags.length > 0 && (
                    <div className={globalStyles["list-tag"]}>
                        <Space size={[4, 4]} wrap>
                            {tags.map(tag => <HashColorTag key={`${organization.id}-${tag}`} value={tag}/>)}
                        </Space>
                    </div>
                )}
            </>
        );
    }

    function renderAction(_value: unknown, organization: Organization) {
        return (
            <Button icon={<DeleteFilled/>} className={"ant-btn-icon-only"}
                    title={"Delete"}
                    onClick={() => {
                        Modal.confirm({
                            content: "Do you really want to delete this organization?",
                            okText: "Delete",
                            cancelText: "Cancel",
                            onOk: () => onDeleteConfirm(organization)
                        });
                    }}/>
        )
    }

    function renderVulReportDefinitionCount(value: number | undefined, organization: Organization) {
        return (
            <Link
                to={`/definitions/vul-reports?${createVulReportDefinitionFilterParam(organization)}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                {value ?? 0}
            </Link>
        );
    }

    function createVulReportDefinitionFilterParam(organization: Organization) {
        const filter = {
            typeHints: {},
            values: {
                organizationIds: [organization.id]
            }
        };

        const searchParams = new URLSearchParams();

        searchParams.set("filter", FormPersister.encodeUrlState(filter));

        return searchParams.toString();
    }

    function onDeleteConfirm(organization: Organization) {
        organizationService.delete(organization)
            .then(() => {
                message.success("Item successfully deleted.");

                reload();
            });
    }

    function reload() {
        return organizationService.getList(tableHandler.queryOptions).then(value => {
            tableHandler.updateTotal(value.total);

            setOrganizations(value);
        });
    }

    function createRqbQuery(values: any) {
        const rqbQuery = {
            combinator: "and",
            rules: []
        }

        if (values.tags?.length > 0) {
            rqbQuery.rules.push({
                field: "tags",
                operator: "arrayIn",
                value: values.tags
            });
        }

        return rqbQuery;
    }

    function onFinishFilter(values: any) {
        tableHandler.onRqbSearchSubmit(createRqbQuery(values));
    }

    function onRow(organization: Organization) {
        return {onDoubleClick: () => navigate(`/organizations/${organization.id}`)}
    }
}

export default OrganizationList;
